import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LogoWhite from '../../assets/icons/logoWhite.svg';
import accessibilityIcon from '../../assets/icons/accessibility.png';
import Procedure from '../../components/Procedure/Procedure';
import Services from '../../components/Services/Services';
import { getTranslation } from '../../components/Specifications/Utils';
import { isUnderWarranty } from '../../utils/helpers/functions.helper';

import { displayGuarantee } from './Utils';

const ACCESSIBILITY_SUFFIX_LANGUAGES = new Set([
  'de',
  'fa',
  'hi',
  'ja',
  'ko',
  'tr',
  'zh-hans',
  'zh-hant',
]);

const getWatchNameTranslationType = (translations) => {
  if (!Array.isArray(translations)) {
    return null;
  }

  const hasModelName = translations.some(
    (translation) =>
      translation?.Type === 'MODEL_NAME' && Boolean(translation.Translation),
  );

  if (hasModelName) {
    return 'MODEL_NAME';
  }

  const hasPortalCatalogLine = translations.some(
    (translation) =>
      translation?.Type === 'PORTAL_CATALOG_LINE' &&
      Boolean(translation.Translation),
  );

  return hasPortalCatalogLine ? 'PORTAL_CATALOG_LINE' : null;
};

const hasServiceExclusions = (service) => {
  return [
    service?.WarrantyExclBra,
    service?.WarrantyExclPrf,
    service?.WarrantyExclWpr,
  ].some((value) => value === 'Y');
};

const PedigreeProduct = ({ rightToLeft, watchData, businessData }) => {
  const { t, i18n } = useTranslation();
  const [isAccessibilityStatementOpen, setIsAccessibilityStatementOpen] =
    useState(false);

  const configuration = watchData?.Configuration;
  const model = configuration?.Model;
  const warrantyEndDate = watchData?.WarrantyEndDate;
  const services = Array.isArray(watchData?.Services) ? watchData.Services : [];
  const serviceInfo = services[0];
  const hasServices = services.length > 0;

  const shouldRenderServices = Boolean(
    serviceInfo?.ServiceType || hasServiceExclusions(serviceInfo),
  );

  const translations = businessData?.[0]?.Translations;
  const watchNameTranslationType = getWatchNameTranslationType(translations);

  const activeLanguage = (
    i18n.resolvedLanguage ||
    i18n.language ||
    ''
  ).toLowerCase();

  const shouldShowAccessibilityEmailSuffix =
    ACCESSIBILITY_SUFFIX_LANGUAGES.has(activeLanguage);

  useEffect(() => {
    if (!isAccessibilityStatementOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsAccessibilityStatementOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAccessibilityStatementOpen]);

  const toggleAccessibilityStatement = () => {
    setIsAccessibilityStatementOpen((isOpen) => !isOpen);
  };

  const closeAccessibilityStatement = () => {
    setIsAccessibilityStatementOpen(false);
  };

  return (
    <>
      <img
        className="logoWhite"
        src={LogoWhite}
        alt={t('accessibility.logoImgAlt')}
      />

      <div className="productPage">
        <div className="content">
          <div className="card">
            <div className="cardContent">
              <div className="watchName">
                {watchNameTranslationType ? (
                  getTranslation(businessData, watchNameTranslationType)
                ) : (
                  <div>ROLEX</div>
                )}
              </div>

              <div className="serialNr">
                {t('serialNr')} {watchData?.SerialNumber}
                {typeof model === 'string' && model && (
                  <div className="referenceNr">
                    {t('reference')}
                    {model.substring(1)}
                  </div>
                )}
              </div>

              {displayGuarantee(
                warrantyEndDate,
                t('underGuarantee'),
                t('serviceGuarantee'),
                t('monthYearFormat'),
              )}
            </div>
          </div>

          {shouldRenderServices && <Services services={services} />}

          <Procedure rightToLeft={rightToLeft} text={t('procedure')} />

          {isUnderWarranty(warrantyEndDate) && hasServices && (
            <div className="card">
              <p className="warrantyLegalNotice">{t('warrantyLegalNotice')}</p>
            </div>
          )}

          {isAccessibilityStatementOpen && (
            <div className="card">
              <div
                id="asccNav"
                className="popupOverlay"
                role="dialog"
                aria-modal="true"
                aria-labelledby="accessibility-statement-title"
              >
                <div className="textWrapperAcc">
                  <button
                    className="close"
                    type="button"
                    aria-label={t('accessibilityStatement.close', {
                      defaultValue: 'Close',
                    })}
                    onClick={closeAccessibilityStatement}
                  >
                    &times;
                  </button>

                  <h1 id="accessibility-statement-title">
                    {t('accessibilityStatement.title')}
                  </h1>

                  <p>{t('accessibilityStatement.info1')}</p>
                  <p>{t('accessibilityStatement.info2')}</p>
                  <p>
                    {t('accessibilityStatement.info3')}{' '}
                    <a href="mailto:accessibility@rolex.com">
                      accessibility@rolex.com
                    </a>
                    {shouldShowAccessibilityEmailSuffix
                      ? t('accessibilityStatement.info4')
                      : ''}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="accessibility">
            <button
              type="button"
              aria-expanded={isAccessibilityStatementOpen}
              aria-controls="asccNav"
              aria-label={t('accessibilityStatement.accessibility')}
              onClick={toggleAccessibilityStatement}
            >
              <img className="languages" src={accessibilityIcon} alt="" />
              {t('accessibilityStatement.accessibility')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PedigreeProduct;
